/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Box, Flex } from '@radix-ui/themes';
import { Container } from 'lucide-react';

import { EmptyState } from '../../../../../shared/EmptyState';
import { ErrorState } from '../../../../../shared/ErrorState';
import { LoadingState } from '../../../../../shared/LoadingState';
import { TablePagination } from '../../../../../shared/TablePagination/TablePagination';
import { NotAvailableTab } from '../NotAvailableTab';
import { PAGE_SIZES } from '../quarantine.types';
import { ContainerTable } from './ContainerTable';
import type { ContainerQuery, QuarantinedContainer } from './containerQuarantine.types';
import { CONTAINER_STRINGS as STRINGS } from './containerQuarantine.strings';
import { useContainerList } from './useContainerList';

import '../QuarantineListView.scss';
import './ContainerListView.scss';

export interface ContainerListViewProps {
  /** The sort and page to start from. */
  initialQuery?: ContainerQuery;
  /** Opens an image's detail, with the list's current sort and page so the detail can link back to them. */
  onOpen?: (item: QuarantinedContainer, query: ContainerQuery) => void;
  /** The detail's URL for an image, with the same sort and page; required with `onOpen`, so the name is a real link. */
  detailHref?: (item: QuarantinedContainer, query: ContainerQuery) => string;
}

export function ContainerListView({ initialQuery, onOpen, detailHref }: ContainerListViewProps = {}): JSX.Element {
  const list = useContainerList(initialQuery);
  const { query, items, total, loading, failed, unavailable, error, hasData } = list;
  // Keep the previous rows mounted during a reload so focus stays on the control that changed.
  const firstLoad = loading && !hasData;
  const reloading = loading && hasData;

  if (unavailable) {
    return <NotAvailableTab />;
  }

  const renderResults = () => {
    if (firstLoad) {
      return <LoadingState message={STRINGS.list.loading} />;
    }
    if (failed) {
      return (
        <ErrorState
          title={STRINGS.list.loadErrorTitle}
          message={error ?? ''}
          onRetry={list.retry}
          retryText={STRINGS.list.retry}
          retryAnalyticsId="nxrm-security-containers-retry-btn"
        />
      );
    }
    if (items.length === 0) {
      return <EmptyState icon={Container} title={STRINGS.list.emptyTitle} description={STRINGS.list.emptyDescription} />;
    }
    return (
      <ContainerTable
        items={items}
        sortBy={query.sortBy}
        asc={query.asc}
        onSort={list.setSort}
        onOpen={onOpen && detailHref ? (item) => onOpen(item, query) : undefined}
        detailHref={onOpen && detailHref ? (item) => detailHref(item, query) : undefined}
      />
    );
  };

  const announcement =
    !hasData || loading || failed ? '' : total === 0 ? STRINGS.list.noResults : STRINGS.results(total.toLocaleString());

  return (
    <Flex direction="column" gap="4">
      <div role="status" aria-live="polite" className="quarantine-visually-hidden">
        {announcement}
      </div>
      <Box
        role="region"
        aria-label="Container quarantine results"
        aria-busy={reloading}
        className={reloading ? 'quarantine-results quarantine-results--busy' : 'quarantine-results'}
      >
        <Flex direction="column" gap="4">
          {renderResults()}
          {total > 0 && !failed && (
            <TablePagination
              currentPage={query.page}
              totalPages={Math.max(1, Math.ceil(total / query.pageSize))}
              itemsPerPage={query.pageSize}
              totalItems={total}
              pageSizeOptions={PAGE_SIZES}
              onPageChange={list.setPage}
              onItemsPerPageChange={list.setPageSize}
              mt="0"
            />
          )}
        </Flex>
      </Box>
    </Flex>
  );
}
